import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Alert, Button, Code, Stack, Text } from '@mantine/core';
import { IconAlertCircle, IconRefresh } from '@tabler/icons-react';

// Catches render errors from lazy-loaded pages or component bugs. Without this
// a thrown error inside a Suspense child shows nothing (just the fallback
// loader forever) because React doesn't propagate errors to Suspense fallback.
//
// On reset we clear our captured state — if the user triggered the error by
// passing bad props (e.g. opening an app that no longer exists), they can hit
// "Try again" after navigating away and back.
type State = { error: Error | null };

export class ErrorBoundary extends Component<{ children: ReactNode; resetKey?: string | number }, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Keep visible in DevTools for debugging. We don't ship a remote logger yet.
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', error, info.componentStack);
  }

  componentDidUpdate(prev: { resetKey?: string | number }) {
    // When the route key changes (parent passes a new resetKey) clear the error
    // so the user can navigate away from a broken page without manual reload.
    if (this.state.error && prev.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  render() {
    if (this.state.error) {
      return (
        <Alert color="red" icon={<IconAlertCircle size={20} />} title="Something broke on this page">
          <Stack gap="sm">
            <Text fz="sm">{this.state.error.message || String(this.state.error)}</Text>
            <Code block style={{ maxHeight: 180, overflow: 'auto', fontSize: 11 }}>
              {(this.state.error.stack || '').slice(0, 1200)}
            </Code>
            <div>
              <Button
                variant="light" size="xs"
                leftSection={<IconRefresh size={14} />}
                onClick={() => { this.setState({ error: null }); }}
              >
                Try again
              </Button>
            </div>
          </Stack>
        </Alert>
      );
    }
    return this.props.children;
  }
}
